import styled from "styled-components";
import { Box } from "../../components/Box";
import { Button } from "../../components/Button";
import { ExpansionPanel } from "../../components/ExpansionPanel";
import { Flex } from "../../components/Flex";
import { H3 } from "../../components/Heading";
import { Paragraph } from "../../components/Paragraph";
import {
  AnswerRow,
  AnswerTable,
  AnswerValue,
  DocumentLink,
  FieldValue,
  FormTitle,
  SectionCard,
  SectionTitle,
} from "../../components/FormAnswers";
import { formatAnswerValue } from "../../utils/formatters";
import { formData, Question } from "../../types/Forms";

/**
 * The Forms panel for an application (Individual View). An application is a
 * single point in time, so each question carries exactly one answer, unlike the
 * Profile Forms tab which aggregates every record attached to the Profile.
 * Both render through the shared FormAnswers kit so the two read the same.
 *
 * This replaces three copies of the same markup: the person and entity tabs in
 * index.tsx and the "All Details" slide-over in IndividualDetaisView.tsx.
 */

const TitleHeading = styled(H3)`
  ${({ theme: { fontSizes } }) => `
    font-size: ${fontSizes[3]};
  `}
`;

const EmptyLine = ({ children }: { children: React.ReactNode }) => (
  <Paragraph fontSize={1} color="gray.60" my={0}>
    {children}
  </Paragraph>
);

/**
 * individual_v2 serialises a file-upload answer's URL onto `document` and the
 * text answer onto `answer`; multi-select is already joined server-side.
 */
const renderAnswer = (question: Question) => {
  const value = formatAnswerValue(question, question.field_type);

  if (question.document) {
    return (
      <FieldValue>
        <DocumentLink href={question.document}>
          {value || "View document"}
        </DocumentLink>
      </FieldValue>
    );
  }

  return value ? (
    <FieldValue>
      <AnswerValue value={value} fieldType={question.field_type} />
    </FieldValue>
  ) : (
    <FieldValue muted>Not answered</FieldValue>
  );
};

export const ApplicationForms = ({
  forms,
  onRequestNew,
}: {
  forms?: formData[];
  /** Omit to render without the "Request New" control. */
  onRequestNew?: () => void;
}) => {
  const list = forms ?? [];

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={2}>
        <TitleHeading>Forms</TitleHeading>
        {onRequestNew && (
          <Button type="button" variant="blueText" onClick={onRequestNew}>
            Request New
          </Button>
        )}
      </Flex>

      {list.length ? (
        list.map((form, fIdx) => (
          <Box key={form.id ?? fIdx} mb={2}>
            <ExpansionPanel
              title={<FormTitle>{form.name}</FormTitle>}
              defaultExpanded
            >
              {form.description && (
                <Paragraph fontSize={1} color="gray.60" mt={0} mb={2}>
                  {form.description}
                </Paragraph>
              )}

              {form.sections?.length ? (
                form.sections.map((section, sIdx) => (
                  <SectionCard key={section.id ?? sIdx}>
                    <SectionTitle>
                      {section.title || `Section ${sIdx + 1}`}
                    </SectionTitle>
                    {section.questions?.length ? (
                      <AnswerTable>
                        {section.questions.map((question, qIdx) => (
                          <AnswerRow
                            key={question.id ?? qIdx}
                            title={question.title}
                            fieldType={question.field_type}
                          >
                            {renderAnswer(question)}
                          </AnswerRow>
                        ))}
                      </AnswerTable>
                    ) : (
                      <FieldValue muted>
                        No questions in this section.
                      </FieldValue>
                    )}
                  </SectionCard>
                ))
              ) : (
                <EmptyLine>No sections in this form.</EmptyLine>
              )}
            </ExpansionPanel>
          </Box>
        ))
      ) : (
        <EmptyLine>
          This section is not included in your application process.
        </EmptyLine>
      )}
    </Box>
  );
};
